/**
 * product-card.js
 * Custom elements: <product-card>, <quick-view-modal>
 * Features: quick-add AJAX, swatch/variant selection, wishlist, quick view
 */

/* ── Cart helpers ──────────────────────────────────────────────────────── */

async function cartAdd(variantId, quantity = 1) {
  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ id: variantId, quantity }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.description || `HTTP ${res.status}`);
  }
  const cart = await fetch('/cart.js').then((r) => r.json());
  return cart;
}

// One /cart.js read shared by every card on the page; reset on each cart:updated.
let cartPromise = null;
function getCart() {
  cartPromise ||= fetch('/cart.js').then((r) => r.json()).catch(() => ({ items: [] }));
  return cartPromise;
}
// cart-drawer:rendered: the drawer's own qty/remove edits only announce themselves this way.
['cart:updated', 'cart-drawer:rendered'].forEach((type) => document.addEventListener(type, (e) => {
  cartPromise = e.detail?.cart ? Promise.resolve(e.detail.cart) : null;
  document.querySelectorAll('product-card').forEach((card) => card._syncStepper?.());
}));

function updateCartUI(itemCount, cart, silent = false) {
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = itemCount;
    el.hidden = itemCount === 0;
    el.setAttribute('aria-label', `Cart: ${itemCount} item${itemCount !== 1 ? 's' : ''}`);
  });
  // silent: stepper tweaks refresh the drawer without popping it open.
  document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count: itemCount, cart, silent } }));
}

/* ── Money formatting ──────────────────────────────────────────────────── */

/**
 * Format cents. Wrapper over money.js — deliberately not named formatMoney,
 * since product.js declares a global of that name and both load on a PDP.
 * @param {number} cents
 */
function formatCardMoney(cents) {
  if (window.Skeleton?.money) return window.Skeleton.money(cents);
  return cents == null ? '' : (cents / 100).toFixed(2);
}

function escHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/* ── Wishlist (localStorage) ───────────────────────────────────────────── */

const WISHLIST_KEY = 'skeleton-wishlist';

function getWishlist() {
  try {
    return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
  } catch {
    return [];
  }
}

function setWishlist(list) {
  localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
}

/* ── Product JSON cache (quick view) ───────────────────────────────────── */

const productCache = new Map();

async function fetchProduct(handle) {
  if (productCache.has(handle)) return productCache.get(handle);
  if (productCache.size >= 50) productCache.delete(productCache.keys().next().value);
  const res = await fetch(`/products/${handle}.js`);
  if (!res.ok) throw new Error(`Product not found: ${handle}`);
  const data = await res.json();
  productCache.set(handle, data);
  return data;
}

/* ── IntersectionObserver singleton (AOS) ──────────────────────────────── */

const _animObserver =
  typeof IntersectionObserver !== 'undefined'
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              _animObserver.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15 }
      )
    : null;

/* ── <product-card> ────────────────────────────────────────────────────── */

class ProductCard extends HTMLElement {
  connectedCallback() {
    this._data = null;
    this._opts = [];
    this._countdownTimer = null;
    this._panelOutsideHandler = null;
    this._panelKeyHandler = null;

    this._parseData();
    this._initSwatches();
    this._initQuickAdd();
    this._initWishlist();
    this._initNotify();
    this._initQuickView();
    this._initOptionsPanel();
    this._initAnim();
    this._initCountdown();
    this._initVideo();
    this._initStepper();
  }

  disconnectedCallback() {
    clearInterval(this._countdownTimer);
    if (this._panelOutsideHandler) document.removeEventListener('click', this._panelOutsideHandler);
    if (this._panelKeyHandler) document.removeEventListener('keydown', this._panelKeyHandler);
  }

  /* Parse embedded JSON -------------------------------------------------- */
  _parseData() {
    const el = this.querySelector('[data-product-json]');
    if (!el) return;
    try {
      this._data = JSON.parse(el.textContent);
      // Seed selected options from first swatch in each group
      this.querySelectorAll('[data-option]').forEach((group) => {
        const pos = parseInt(group.dataset.option, 10) - 1;
        const firstBtn = group.querySelector('.product-card__opt-btn');
        if (firstBtn) this._opts[pos] = firstBtn.dataset.optValue;
      });
    } catch (e) {
      console.warn('product-card: could not parse product JSON', e);
    }
  }

  /* Swatch / variant selection ------------------------------------------- */
  _initSwatches() {
    this.querySelectorAll('[data-opt-value]').forEach((btn) => {
      btn.addEventListener('click', (e) => this._onSwatchClick(e.currentTarget));
    });

    // "+N more": the hidden options are already in the DOM with handlers bound,
    // so revealing them is one attribute each and nothing needs re-binding.
    this.querySelectorAll('[data-opt-more]').forEach((more) => {
      more.addEventListener('click', () => {
        more.closest('.product-card__option')
          ?.querySelectorAll('.product-card__opt-btn[hidden]')
          .forEach((el) => { el.hidden = false; });
        more.hidden = true;
      });
    });

    // variant_display pills are separate products, so switching re-points the
    // whole card rather than picking another variant of this one.
    this.querySelectorAll('[data-vp-variant]').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        this._onSiblingProductClick(e.currentTarget);
      });
    });
  }

  _onSwatchClick(btn) {
    const pos = parseInt(btn.dataset.optPos, 10) - 1;

    this._selectInGroup(btn);
    this._opts[pos] = btn.dataset.optValue;

    const variant = this._findVariant(pos);
    if (variant) this._applyVariant(variant);
  }

  // Marks one option in its group as the chosen one.
  _selectInGroup(el) {
    el.closest('.product-card__option')
      ?.querySelectorAll('.product-card__opt-btn')
      .forEach((b) => {
        b.classList.remove('product-card__opt-btn--selected');
        b.removeAttribute('aria-current');
      });
    el.classList.add('product-card__opt-btn--selected');
    el.setAttribute('aria-current', 'true');
  }

  // Re-points the card at a sibling product from custom.variant_display, using
  // only the link's data- attributes — no fetch, no second JSON payload.
  _onSiblingProductClick(link) {
    const d = link.dataset;
    this._selectInGroup(link);

    const variant = {
      id: d.vpVariant,
      price: parseInt(d.vpPrice, 10) || 0,
      compare_at_price: parseInt(d.vpCompare, 10) || 0,
      available: d.vpAvailable === 'true',
    };

    this._siblingVariant = variant;

    const input = this.querySelector('[data-variant-id]');
    if (input) input.value = variant.id;

    this._renderPrice(variant);
    this._renderPoints(variant, Number(d.vpPointsRate));
    this._updateCTA(variant);
    if (d.vpImg) this._swapImage(d.vpImg, d.vpTitle || '');

    // The card's own links must follow, or the title and image still open the
    // product the shopper just switched away from.
    this.querySelectorAll('.product-card__title-link, .product-card__image-link')
      .forEach((a) => { a.href = d.vpUrl; });
    const title = this.querySelector('.product-card__title-link');
    if (title && d.vpTitle) title.textContent = d.vpTitle;
  }

  _findVariant(changedPos) {
    if (!this._data) return null;
    const { variants } = this._data;

    // Exact match across all selected options
    const exact = variants.find((v) =>
      this._opts.every((sel, i) => sel == null || v[`option${i + 1}`] === sel)
    );
    if (exact) return exact;

    // Partial: just changed option matches + available
    const partial = variants.find(
      (v) => v[`option${changedPos + 1}`] === this._opts[changedPos] && v.available
    );
    if (partial) return partial;

    // Fallback: just changed option, any availability
    return (
      variants.find((v) => v[`option${changedPos + 1}`] === this._opts[changedPos]) ||
      variants[0]
    );
  }

  _applyVariant(variant) {
    // --- form hidden input
    const input = this.querySelector('[data-variant-id]');
    if (input) input.value = variant.id;

    /* --- price
       This used to skip range-priced products, on the assumption the card was
       showing "From £x" and a swatch could not change it. The card now renders
       the selected variant's own price whenever variant pills exist, so a range
       is exactly the case that has to update. */
    if (this._data) this._renderPrice(variant);

    // --- loyalty points, priced off the same variant as the price above
    this._renderPoints(variant);

    // --- CTA availability
    this._updateCTA(variant);

    // --- image swap
    const imgId = variant.featured_image?.id;
    if (imgId && this._data.images[imgId]) {
      const { src, alt } = this._data.images[imgId];
      this._swapImage(src, alt || '');
    }
  }

  _renderPrice(variant) {
    const wrap = this.querySelector('[data-price-wrapper]');
    if (!wrap) return;

    /* % off flag on the image, rounded up — same rule as the Liquid: offer % wins, else compare-at. */
    const offEl = this.querySelector('[data-card-off]');
    if (offEl) {
      const discPct = Math.ceil(parseFloat(this.dataset.discountPct) || 0);
      const offPct = discPct > 0 ? discPct
        : variant.compare_at_price > variant.price
          ? Math.ceil(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price)
          : 0;
      offEl.textContent = `${offPct}% off`;
      offEl.hidden = offPct === 0;
    }

    /* Merchant offer (sections/discount-offer.liquid). Without this the first
       variant-pill click would repaint the card at full price. Floor-divided
       cents, matching the Liquid in snippets/product-card.liquid. */
    const pct = parseInt(this.dataset.discountPct, 10) || 0;
    if (pct > 0) {
      const off = Math.floor((variant.price * pct) / 100);
      wrap.innerHTML = `
        <span class="product-card__price-sale">${formatCardMoney(variant.price - off)}</span>
        <s class="product-card__price-compare">${formatCardMoney(variant.price)}</s>
      `;
      return;
    }

    const isOnSale = variant.compare_at_price > variant.price;
    if (isOnSale) {
      wrap.innerHTML = `
        <span class="product-card__price-sale">${formatCardMoney(variant.price)}</span>
        <s class="product-card__price-compare">${formatCardMoney(variant.compare_at_price)}</s>
      `;
    } else {
      wrap.innerHTML = `<span class="product-card__price-regular" data-price>${formatCardMoney(variant.price)}</span>`;
    }
  }

  /* Whole units of spend only — £2.99 at 10 points per £1 earns 20, not 29.
     The rate comes from window.theme.loyalty (layout/theme.liquid), the same
     source assets/product.js uses, so a card and a PDP cannot disagree. */
  _renderPoints(variant, rateOverride) {
    const pill = this.querySelector('.product-card__points');
    if (!pill) return;
    const { rate: baseRate = 0, spendCents = 0 } = window.theme?.loyalty || {};
    /* A campaign (2x points on a collection, on given days) lifts the rate for
       some products only. The pill carries this product's rate; a sibling from
       custom.variant_display passes its own, since it is a different product
       with its own collections. */
    const rate = rateOverride || Number(pill.dataset.pointsRate) || baseRate;
    const total = spendCents > 0 ? Math.floor(variant.price / spendCents) * rate : 0;
    pill.querySelector('[data-points-value]').textContent = String(total);
    pill.hidden = total <= 0;
  }

  _swapImage(src, alt) {
    const primary = this.querySelector('.product-card__image--primary');
    if (!primary) return;
    const file = src.split('?')[0].split('/').pop();
    if (file && (primary.currentSrc || primary.src).includes(file)) return;
    primary.style.opacity = '0';
    const tmp = new Image();
    tmp.onload = () => {
      primary.removeAttribute('srcset');
      primary.removeAttribute('sizes');
      primary.src = src;
      primary.alt = alt;
      primary.style.opacity = '';
    };
    tmp.src = src;
  }

  _updateCTA(variant) {
    // Cards can render two ATC buttons (body CTA + options panel). Update both.
    this.querySelectorAll('[data-quick-add]').forEach((btn) => {
      if (!variant.available) {
        btn.disabled = true;
        const label = btn.querySelector('.product-card__btn-label');
        if (label) label.textContent = btn.dataset.soldOutLabel || 'Sold out';
      } else {
        btn.disabled = false;
        const label = btn.querySelector('.product-card__btn-label');
        if (label) label.textContent = btn.dataset.addLabel || 'Add to cart';
      }
    });
    this._syncStepper();
  }

  /* Qty stepper: the + square becomes − n + once the variant is in the cart */
  _initStepper() {
    this._stepper = this.querySelector('[data-card-stepper]');
    if (!this._stepper) return;
    this._stepper.addEventListener('click', (e) => {
      const b = e.target.closest('[data-step]');
      if (b) this._step(Number(b.dataset.step));
    });
    this._syncStepper();
  }

  async _syncStepper() {
    if (!this._stepper || this.hasAttribute('data-demo')) return;
    // A cart read that started before (or during) a stepper write is stale:
    // applying it flashed the stepper back at 1 after pressing − to 0.
    if (this._stepping) return;
    const rev = this._rev;
    const id = Number(this.querySelector('[data-variant-id]')?.value);
    const cart = await getCart();
    if (this._stepping || rev !== this._rev) return;
    const line = cart.items?.find((i) => i.variant_id === id);
    this._line = line || null;
    this._showStepper(line?.quantity || 0);
  }

  _showStepper(qty) {
    const addBtn = this.querySelector('.product-card__btn--icon');
    this._stepper.hidden = qty < 1;
    if (addBtn) addBtn.hidden = qty > 0;
    this._stepper.querySelector('[data-step-qty]').textContent = qty;
  }

  async _step(delta) {
    if (!this._line || this._stepping) return;
    const qty = Math.max(0, this._line.quantity + delta);
    this._stepping = true;
    this._rev = (this._rev || 0) + 1;
    this._showStepper(qty); // optimistic
    let cart = null;
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: this._line.key, quantity: qty }),
      });
      cart = await res.json();
      if (!res.ok) throw new Error(cart.description || `HTTP ${res.status}`);
    } catch (err) {
      console.error('product-card stepper:', err.message);
      cart = null;
      cartPromise = null;
    }
    // Cleared first so the sync this triggers is allowed to apply.
    this._stepping = false;
    if (cart) updateCartUI(cart.item_count, cart, true);
    else this._syncStepper();
  }

  /* Quick add ------------------------------------------------------------ */
  _initQuickAdd() {
    const form = this.querySelector('[data-product-form]');
    if (!form) return;
    form.addEventListener('submit', (e) => this._onQuickAdd(e));
  }

  _resolveQuickAddBtn(e) {
    // Prefer the submitter — multi-variant cards have a hidden panel ATC that
    // precedes the visible body CTA in the DOM, so querySelector alone always
    // targeted the wrong button and the loading spinner never appeared.
    const submitter = e?.submitter;
    if (submitter?.matches?.('[data-quick-add]') && this.contains(submitter)) {
      return submitter;
    }
    const fromPath = submitter?.closest?.('[data-quick-add]');
    if (fromPath && this.contains(fromPath)) return fromPath;
    return this.querySelector('.product-card__cta [data-quick-add]')
      || this.querySelector('[data-quick-add]');
  }

  async _onQuickAdd(e) {
    e.preventDefault();
    const btn = this._resolveQuickAddBtn(e);
    const variantId = parseInt(this.querySelector('[data-variant-id]')?.value, 10);
    if (!variantId) return;

    this._setAddState('loading', btn);

    if (this.hasAttribute('data-demo')) {
      await new Promise((r) => setTimeout(r, 750));
      this._setAddState('success', btn);
      setTimeout(() => { this._setAddState('idle', btn); this._closePanel(); }, 2000);
      return;
    }

    try {
      const cart = await cartAdd(variantId, 1);
      this._setAddState('success', btn);
      updateCartUI(cart.item_count, cart);
      setTimeout(() => { this._setAddState('idle', btn); this._closePanel(); }, 2000);
    } catch (err) {
      console.error('product-card quick-add:', err.message);
      this._setAddState('error', btn);
      setTimeout(() => this._setAddState('idle', btn), 2500);
    }
  }

  _setAddState(state, btn) {
    if (!btn) return;

    if (state === 'idle') {
      delete btn.dataset.state;
      btn.setAttribute('aria-busy', 'false');
      const variantId = this.querySelector('[data-variant-id]')?.value;
      // A variant_display sibling is a different product, so it is never in
      // this card's own variant list. Without the fallback the lookup misses
      // and the else branch below re-enables the button even for a sold-out
      // sibling.
      const variant =
        this._data?.variants?.find((v) => String(v.id) === String(variantId)) ||
        (String(this._siblingVariant?.id) === String(variantId) ? this._siblingVariant : null);
      if (variant) {
        this._updateCTA(variant);
      } else {
        this.querySelectorAll('[data-quick-add]').forEach((b) => { b.disabled = false; });
      }
      return;
    }

    btn.dataset.state = state;
    btn.disabled = state === 'loading';
    btn.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');

    // Keep sibling ATC buttons disabled while a request is in flight
    const busy = state === 'loading' || state === 'success';
    this.querySelectorAll('[data-quick-add]').forEach((other) => {
      if (other === btn) return;
      other.disabled = busy;
    });
  }

  /* Wishlist ------------------------------------------------------------- */
  _initWishlist() {
    const btn = this.querySelector('[data-wishlist]');
    if (!btn) return;
    const handle = btn.dataset.wishlist;

    // Restore from localStorage
    if (getWishlist().includes(handle)) {
      btn.setAttribute('aria-pressed', 'true');
    }

    btn.addEventListener('click', () => {
      const list = getWishlist();
      const active = btn.getAttribute('aria-pressed') === 'true';
      if (active) {
        setWishlist(list.filter((h) => h !== handle));
        btn.setAttribute('aria-pressed', 'false');
      } else {
        list.push(handle);
        setWishlist(list);
        btn.setAttribute('aria-pressed', 'true');
      }
      document.dispatchEvent(
        new CustomEvent('wishlist:updated', { detail: { handle, active: !active } })
      );
    });
  }

  /* Notify me ------------------------------------------------------------ */
  // Cards have no email field of their own; the real notify-me form lives in the
  // PDP buy_buttons block, so send the shopper there. Without this the button
  // rendered on every sold-out card and did nothing at all.
  _initNotify() {
    this.querySelectorAll('[data-notify]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const handle = btn.dataset.notify;
        if (!handle) return;
        const root = window.Shopify?.routes?.root || '/';
        window.location.href = `${root.replace(/\/$/, '')}/products/${handle}#notify`;
      });
    });
  }

  /* Quick view ----------------------------------------------------------- */
  _initQuickView() {
    this.querySelectorAll('[data-quick-view]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const modal = document.querySelector('quick-view-modal') || QuickViewModal.create();
        modal.open(btn.dataset.quickView, btn);
      });
    });
  }

  /* Options panel -------------------------------------------------------- */
  _initOptionsPanel() {
    if (!this.querySelector('.product-card__panel')) return;

    this._panelOutsideHandler = (e) => {
      if (!this.contains(e.target)) this._closePanel();
    };
    this._panelKeyHandler = (e) => {
      if (e.key === 'Escape') this._closePanel();
    };

    this.querySelector('[data-open-panel]')?.addEventListener('click', () => this._openPanel());
    this.querySelector('[data-close-panel]')?.addEventListener('click', () => this._closePanel());
  }

  _openPanel() {
    if (this.classList.contains('is-panel-open')) return;
    const panel = this.querySelector('.product-card__panel');
    if (!panel) return;
    this.classList.add('is-panel-open');
    panel.setAttribute('aria-hidden', 'false');
    this.querySelector('[data-open-panel]')?.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', this._panelOutsideHandler);
    document.addEventListener('keydown', this._panelKeyHandler);
  }

  _closePanel() {
    const panel = this.querySelector('.product-card__panel');
    if (!panel || !this.classList.contains('is-panel-open')) return;
    this.classList.remove('is-panel-open');
    panel.setAttribute('aria-hidden', 'true');
    const trigger = this.querySelector('[data-open-panel]');
    trigger?.setAttribute('aria-expanded', 'false');
    trigger?.focus();
    document.removeEventListener('click', this._panelOutsideHandler);
    document.removeEventListener('keydown', this._panelKeyHandler);
  }

  /* Entrance animation (IntersectionObserver) ---------------------------- */
  _initAnim() {
    if (!this.hasAttribute('data-anim')) return;
    if (_animObserver) {
      _animObserver.observe(this);
    } else {
      this.classList.add('is-visible');
    }
  }

  /* Sale countdown timer ------------------------------------------------- */
  _initCountdown() {
    const el = this.querySelector('[data-countdown]');
    if (!el) return;

    const endMs = parseInt(el.dataset.countdown, 10) * 1000;

    const pad = (n) => String(n).padStart(2, '0');

    const update = () => {
      const diff = endMs - Date.now();
      if (diff <= 0) {
        el.textContent = window.theme?.strings?.product?.saleEnded || 'Sale ended';
        clearInterval(this._countdownTimer);
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      el.textContent = h > 0 ? `${h}h ${pad(m)}m ${pad(s)}s` : `${pad(m)}m ${pad(s)}s`;
    };

    update();
    this._countdownTimer = setInterval(update, 1000);
  }

  /* Video hover play/pause ----------------------------------------------- */
  _initVideo() {
    const video = this.querySelector('.product-card__video');
    if (!video) return;
    video.muted = true;

    this.addEventListener('mouseenter', () => {
      video.play().catch(() => {});
    });
    this.addEventListener('mouseleave', () => {
      video.pause();
      video.currentTime = 0;
    });
  }
}

/* ── <quick-view-modal> ────────────────────────────────────────────────── */

class QuickViewModal extends HTMLElement {
  static create() {
    const el = document.createElement('quick-view-modal');
    el.innerHTML = `
      <dialog class="quick-view__dialog" aria-label="Quick view">
        <button type="button" class="quick-view__close" aria-label="Close quick view">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
        <div data-modal-content></div>
      </dialog>
    `;
    document.body.appendChild(el);
    return el;
  }

  connectedCallback() {
    this._dialog = this.querySelector('dialog');
    this._content = this.querySelector('[data-modal-content]');
    this._trigger = null;

    this.querySelector('.quick-view__close')?.addEventListener('click', () => this.close());

    // Click outside closes
    this._dialog?.addEventListener('click', (e) => {
      if (e.target === this._dialog) this.close();
    });

    // Escape handled natively by <dialog>, but also wire it up explicitly
    this._dialog?.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.close();
    });
  }

  async open(handle, triggerEl) {
    this._trigger = triggerEl || null;
    this._dialog.dataset.loading = 'true';
    this._content.innerHTML = '';
    this._dialog.showModal();
    document.body.classList.add('modal-open');

    // Demo mode: render from inline <script data-demo-content> instead of fetching
    const card = triggerEl?.closest('product-card');
    const demoEl = card?.querySelector('[data-demo-content]');
    if (card?.hasAttribute('data-demo') && demoEl) {
      try {
        this._render(JSON.parse(demoEl.textContent));
      } catch {
        this._content.innerHTML = `<p class="quick-view__error">${window.theme?.strings?.product?.quickViewError || 'Content unavailable.'}</p>`;
      }
      delete this._dialog.dataset.loading;
      return;
    }

    try {
      const product = await fetchProduct(handle);
      this._render(product);
    } catch {
      this._content.innerHTML = `<p class="quick-view__error">Could not load product. <a href="/products/${escHtml(handle)}">View product page →</a></p>`;
    } finally {
      delete this._dialog.dataset.loading;
    }
  }

  close() {
    this._dialog.close();
    document.body.classList.remove('modal-open');
    this._trigger?.focus();
    this._trigger = null;
  }

  _render(product) {
    const variant = product.variants.find((v) => v.available) || product.variants[0];
    const img = product.images?.[0];
    const isOnSale = variant.compare_at_price > variant.price;
    const isDemo = !product.url || product.url === '#';

    const mediaHtml = img?.src
      ? `<img src="${escHtml(img.src)}" alt="${escHtml(img.alt || product.title)}" loading="lazy" class="quick-view__image">`
      : `<div class="quick-view__no-image" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5" fill="currentColor" width="80" height="80"><path d="M324.5 212.7H203c-1.6 0-2.8 1.3-2.8 2.8V308c0 1.6 1.3 2.8 2.8 2.8h121.6c1.6 0 2.8-1.3 2.8-2.8v-92.5c0-1.6-1.3-2.8-2.9-2.8zm1.1 95.3c0 .6-.5 1.1-1.1 1.1H203c-.6 0-1.1-.5-1.1-1.1v-92.5c0-.6.5-1.1 1.1-1.1h121.6c.6 0 1.1.5 1.1 1.1V308z"/><path d="M210.4 299.5H240v.1s.1 0 .2-.1h75.2v-76.2h-105v76.2zm74.3-39.8c1.4-1.4 3.8-1.4 5.2 0l14.4 14.4 5.2-5.2c1.4-1.4 3.8-1.4 5.2 0l12.5 12.5v17.2h-101v-2.5l28.5-28.5c1.4-1.4 3.8-1.4 5.2 0l17.5 17.5 7.3-25.4z"/><path d="M232.3 269.8c3.8 0 6.9-3.1 6.9-6.9s-3.1-6.9-6.9-6.9-6.9 3.1-6.9 6.9 3.1 6.9 6.9 6.9z"/></svg></div>`;

    this._content.innerHTML = `
      <div class="quick-view__layout">
        <div class="quick-view__media">${mediaHtml}</div>
        <div class="quick-view__info">
          <h2 class="quick-view__title">${escHtml(product.title)}</h2>
          <div class="quick-view__price">
            ${isOnSale
              ? `<span class="product-card__price-sale">${formatCardMoney(variant.price)}</span>
                 <s class="product-card__price-compare">${formatCardMoney(variant.compare_at_price)}</s>`
              : `<span class="product-card__price-regular">${formatCardMoney(variant.price)}</span>`}
          </div>
          ${product.description
            ? `<p class="quick-view__desc">${escHtml(product.description)}</p>`
            : ''}
          ${isDemo
            ? `<span class="quick-view__cta quick-view__cta--demo" aria-disabled="true">Demo product</span>`
            : `<a href="${escHtml(product.url)}" class="quick-view__cta">View full details</a>`}
        </div>
      </div>
    `;
  }
}

/* ── Register ──────────────────────────────────────────────────────────── */

if (!customElements.get('product-card')) customElements.define('product-card', ProductCard);
if (!customElements.get('quick-view-modal')) customElements.define('quick-view-modal', QuickViewModal);

/* ── "+ More" size picker (snippets/variant-modal.liquid) ───────────────── */
(function () {
  function money(cents, format) {
    if (window.Skeleton && window.Skeleton.money) return window.Skeleton.money(cents);
    const amount = (cents / 100).toFixed(2);
    // Fall back to the shop's money_format so the currency symbol is right
    // even when money.js has not loaded on this page.
    return format ? format.replace(/\{\{\s*amount[^}]*\}\}/, amount) : amount;
  }

  class VariantModal extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      // The card (and the reveal animation's transform) is a containing block
      // for position:fixed and clips overflow, so a modal left inside it lands
      // wherever the card is. Reparenting to <body> is what puts it in the
      // middle of the viewport.
      if (this.parentElement !== document.body) document.body.appendChild(this);

      this._format = this.dataset.moneyFormat || '';
      this._confirm = this.querySelector('[data-vmodal-confirm]');
      this._total = this.querySelector('[data-vmodal-total]');

      this.addEventListener('click', this._onClick.bind(this));
      this._onKeydown = (e) => { if (e.key === 'Escape') this.close(); };
    }

    open(trigger) {
      this._trigger = trigger || null;
      this.hidden = false;
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this._onKeydown);
      this.querySelector('[data-vmodal-close]')?.focus();
    }

    close() {
      this.hidden = true;
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this._onKeydown);
      this._trigger?.focus();
    }

    _onClick(e) {
      if (e.target.closest('[data-vmodal-close]')) {
        this.close();
        return;
      }

      const step = e.target.closest('[data-vmodal-step]');
      if (step) {
        this._step(step.closest('[data-vmodal-tile]'), Number(step.dataset.vmodalStep));
        return;
      }

      if (e.target.closest('[data-vmodal-confirm]')) this._submit();
    }

    _step(tile, delta) {
      if (!tile) return;
      const countEl = tile.querySelector('[data-vmodal-count]');
      const next = Math.max(0, Number(countEl.textContent || 0) + delta);
      countEl.textContent = next;
      tile.classList.toggle('is-picked', next > 0);
      this._sync();
    }

    _lines() {
      return Array.from(this.querySelectorAll('[data-vmodal-tile]'))
        .map((tile) => ({
          id: Number(tile.dataset.variantId),
          quantity: Number(tile.querySelector('[data-vmodal-count]')?.textContent || 0),
          price: Number(tile.dataset.price || 0),
        }))
        .filter((line) => line.id && line.quantity > 0);
    }

    _sync() {
      const lines = this._lines();
      const cents = lines.reduce((sum, l) => sum + l.price * l.quantity, 0);
      if (this._total) this._total.textContent = money(cents, this._format);
      if (this._confirm) this._confirm.disabled = lines.length === 0;
    }

    async _submit() {
      const lines = this._lines();
      if (!lines.length || this._busy) return;
      this._busy = true;
      this._confirm.classList.add('is-busy');

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: lines.map(({ id, quantity }) => ({ id, quantity })) }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const cart = await fetch('/cart.js').then((r) => r.json());
        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));

        // Reset the picker so reopening it starts clean
        this.querySelectorAll('[data-vmodal-tile]').forEach((tile) => {
          tile.querySelector('[data-vmodal-count]').textContent = '0';
          tile.classList.remove('is-picked');
        });
        this._sync();
        this.close();
      } catch (err) {
        this._confirm.classList.add('is-error');
        window.setTimeout(() => this._confirm.classList.remove('is-error'), 2000);
      } finally {
        this._busy = false;
        this._confirm.classList.remove('is-busy');
      }
    }
  }

  if (!customElements.get('variant-modal')) customElements.define('variant-modal', VariantModal);

  // One delegated listener for every card on the page.
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-vmodal-open]');
    if (!trigger) return;
    const modal = document.getElementById(trigger.dataset.vmodalOpen);
    if (!modal) return;
    e.preventDefault();
    modal.open(trigger);
  });
})();
